import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, addDoc } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import InfoStep from './InfoStep';
import InfoCarouselStep from './InfoCarouselStep';
import ActionStep from './ActionStep';
import QuestionStep from './QuestionStep';
import ReflectionStep from './ReflectionStep';
import BreathingExerciseStep from './BreathingExerciseStep';
import SummaryStep from './SummaryStep';
import './ExerciseSteps.css';

const ExerciseController = ({ exerciseData }) => {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [userResponses, setUserResponses] = useState({});
  const navigate = useNavigate();

  const currentStep = exerciseData.steps[currentStepIndex];
  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === exerciseData.steps.length - 1;
  const progress = ((currentStepIndex + 1) / exerciseData.steps.length) * 100;

  // Save response to Firestore asynchronously (fire-and-forget)
  const saveResponseToFirestore = async (stepData, userResponse) => {
    try {
      const userId = auth.currentUser?.uid || localStorage.getItem('user_id') || 'anonymous';

      await addDoc(collection(db, 'responses'), {
        exercise_name: exerciseData.title,
        step_title: stepData.title,
        response: userResponse,
        user_id: userId,
        timestamp: new Date()
      });

      console.log('Response saved to Firestore');
    } catch (error) {
      console.error('Error saving response to Firestore:', error);
    }
  };

  const handleStepComplete = (response = null) => {
    // Check if response is valid data (not a React event object)
    const isValidResponse = response !== null &&
      typeof response !== 'undefined' &&
      !response?.nativeEvent; // Filter out React SyntheticEvents

    // Store user response if provided and valid
    if (isValidResponse) {
      setUserResponses(prev => ({
        ...prev,
        [currentStepIndex]: response
      }));

      // Save to Firestore with actual response
      saveResponseToFirestore(currentStep, response);
    } else {
      // For informational steps, save that user continued through this step
      saveResponseToFirestore(currentStep, { action: 'continued' });
    }

    // Move to next step or finish
    if (isLastStep) {
      // Exercise completed
      navigate('/enojo');
    } else {
      setCurrentStepIndex(prev => prev + 1);
    }
  };

  const renderStepComponent = () => {
    const commonProps = {
      step: currentStep,
      onContinue: handleStepComplete,
      isFirstStep
    };

    switch (currentStep.type) {
      case 'info':
        return <InfoStep {...commonProps} />;
      case 'info_carousel':
        return <InfoCarouselStep {...commonProps} />;
      case 'action':
        return <ActionStep {...commonProps} />;
      case 'question':
        return <QuestionStep {...commonProps} />;
      case 'reflection':
        return <ReflectionStep {...commonProps} />;
      case 'guided_breathing':
        return <BreathingExerciseStep {...commonProps} />;
      case 'summary':
        return <SummaryStep {...commonProps} />;
      default:
        return <div>Paso no reconocido: {currentStep.type}</div>;
    }
  };

  return (
    <div className="exercise-controller">
      {/* Progress Bar */}
      <div className="progress-container">
        <div className="progress-info">
          <span className="progress-text">
            Paso {currentStepIndex + 1} de {exerciseData.steps.length}
          </span>
          <span className="progress-percentage">
            {Math.round(progress)}%
          </span>
        </div>
        <div className="progress-bar-container">
          <div
            className="progress-bar-fill"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Exercise Title */}
      <div className="exercise-header">
        <h1 className="exercise-title">{exerciseData.title}</h1>
        <p className="exercise-summary">{exerciseData.summary}</p>
      </div>

      {/* Current Step */}
      <div className="step-wrapper">
        {renderStepComponent()}
      </div>
    </div>
  );
};

export default ExerciseController; 